import { Box, Flex, Stack, Text, TextInput } from '@mantine/core';
import { Button as DesignButton } from 'src/design-system/Button';
import { FlowNavigationFooter } from 'src/design-system/FlowNavigationFooter';
import { Select } from 'src/design-system/Select';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { Client } from 'src/admin/users/types';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { V2MemberRoles } from './dataHelpers';
import { FamilyRole } from '../hooks/useFamilyRoles';

type Props = {
  selectedMembers: Client[];
  roleMode: boolean;
  familyRoles: FamilyRole[];
  onBack: () => void;
  onCancel: () => void;
  onSubmit: (familyName: string, roles: V2MemberRoles) => void;
  isLoading: boolean;
};

export default function NameAndRelationshipsStep({
  selectedMembers,
  roleMode,
  familyRoles,
  onBack,
  onCancel,
  onSubmit,
  isLoading,
}: Props) {
  const [familyName, setFamilyName] = useState('');
  const [focused, setFocused] = useState(false);
  const [roles, setRoles] = useState<V2MemberRoles>(() =>
    Object.fromEntries(selectedMembers.map(m => [m.id, null])),
  );

  const roleOptions = familyRoles.map(role => ({ value: role.id, label: role.name }));
  const rolesAvailable = roleOptions.length > 0;

  // In role mode a role is optional (the backend column is nullable). In the
  // flag-off star fallback every non-primary member's role becomes their
  // relationship-to-main, so each one must be set for the backend to derive the
  // legacy shape. The first member is the main member and never needs a role.
  // If no roles are available (backend not yet seeded), we don't block: the
  // payload falls back to an "Other" relationship for every secondary.
  const secondaries = selectedMembers.slice(1);
  const allSecondaryRolesSet = secondaries.every(m => roles[m.id]);
  const isValid =
    familyName.trim().length > 0 && (roleMode || !rolesAvailable || allSecondaryRolesSet);

  const setRole = (memberId: string, roleId: string | null) => {
    setRoles(prev => ({ ...prev, [memberId]: roleId }));
  };

  return (
    <Flex direction="column" h="100%">
      <FlowLayout
        header={
          <Box px="xl" pt="xxl" pb="md">
            <Flex justify="space-between" align="center" pb="lg">
              <Text c="gray.10" size="displaySm" fw={FONT_WEIGHTS.semibold}>
                Name this family and set family roles
              </Text>
              <DesignButton variant="text" style={{ flexShrink: 0 }} onClick={onCancel}>
                Cancel
              </DesignButton>
            </Flex>

            <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.6" mb={6}>
              Family name
            </Text>
            <TextInput
              placeholder="Family name"
              value={familyName}
              onChange={e => setFamilyName(e.currentTarget.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              rightSection={
                familyName.trim().length > 0 ? (
                  <IconCheck size={18} color="var(--mantine-color-green-6)" />
                ) : (
                  <IconX size={18} color="var(--mantine-color-red-6)" />
                )
              }
              radius="xl"
              size="md"
              styles={theme => ({
                wrapper: { height: 48 },
                input: {
                  height: 48,
                  borderRadius: 9999,
                  backgroundColor: theme.white,
                  border: `2px solid ${focused ? theme.colors.brand[6] : theme.colors.gray[2]}`,
                  fontSize: theme.fontSizes.md,
                },
                section: {
                  width: 44,
                  justifyContent: 'center',
                },
              })}
            />
          </Box>
        }
        body={
          <FlowBody
            footerAlign="center"
            footer={
              <FlowNavigationFooter
                onBack={onBack}
                onAction={() => onSubmit(familyName, roles)}
                actionDisabled={!isValid}
                actionLoading={isLoading}
              />
            }
          >
            <Box px="xs" pb="md">
              <Stack gap="sm">
                {selectedMembers.map((client, index) => {
                  const roleRequired = !roleMode && rolesAvailable && index > 0;
                  const roleMissing = roleRequired && !roles[client.id];
                  return (
                    <Box key={client.id} w="100%" bg="gray.0" style={{ borderRadius: 20 }}>
                      <Flex justify="space-between" align="center">
                        <Box style={{ paddingTop: 23, paddingBottom: 23, paddingLeft: 20 }}>
                          <Text fw={FONT_WEIGHTS.semibold} size="md" c="gray.10">
                            {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
                          </Text>
                          {client.emailAddress && (
                            <Text
                              size="sm"
                              fw={FONT_WEIGHTS.medium}
                              c="gray.7"
                              style={{ marginTop: 2 }}
                            >
                              {client.emailAddress}
                            </Text>
                          )}
                        </Box>

                        <Box style={{ paddingTop: 16, paddingBottom: 16, paddingRight: 20 }}>
                          <Text
                            size="xs"
                            fw={FONT_WEIGHTS.semibold}
                            c={roleMissing ? 'red.6' : 'gray.7'}
                            style={{ marginBottom: 6 }}
                          >
                            Family role{roleRequired ? ' *' : ''}
                          </Text>
                          <Select
                            placeholder="Select role"
                            data={roleOptions}
                            value={roles[client.id] ?? null}
                            onChange={val => setRole(client.id, val)}
                            variant="card"
                            clearable={roleMode}
                            styles={{
                              input: {
                                height: 'auto',
                                minHeight: 'unset',
                                color: 'black',
                                fontSize: 'var(--mantine-font-size-sm)',
                                fontWeight: FONT_WEIGHTS.medium,
                                ...(roleMissing
                                  ? { border: '1px solid var(--mantine-color-red-6)' }
                                  : {}),
                              },
                            }}
                          />
                        </Box>
                      </Flex>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </FlowBody>
        }
      />
    </Flex>
  );
}
